import { AfterViewInit, ChangeDetectorRef, Component, effect, Input, OnInit } from '@angular/core';
import { PatientSessionService } from '../patient-session.service';
import { AlarmOption, PatientTargetDataModel, PatientTargetModel, PatientTargetStatus } from '../../../core/api/models';
import { BaseBoxComponent } from '../base-box/base-box.component';
import { AlertService } from '../../../shared/services/alert.service';
import { isOpenEndedFluencyRate as targetIsOpenEndedTimer } from '../../../shared/utils/utils';
@Component({
  selector: 'app-box-rate',
  templateUrl: './box-rate.component.html',
  standalone: false,
})
export class BoxRateComponent extends BaseBoxComponent implements AfterViewInit, OnInit {
  text: string | undefined = '-';
  expectedAmount: number = 0;
  @Input() isTopBox: boolean = true;
  @Input() isBottomBox: boolean = false;
  @Input() target: PatientTargetModel | undefined = undefined;
  @Input() isPreview?: boolean;
  timerInitialValue: number = 60;
  targetID: number = 0;
  targetData: PatientTargetDataModel[] = [];

  isStarted: boolean = false;
  timerValue: number = 0;
  private startTime: number = 0;
  rates: { totalCount: number; Duration: number; RecordedDate: string; FormattedDate: string }[] = [];
  totalCount: number = 0;
  timeOut: any;
  remainingTrials: number = 0;

  constructor(
    protected override cdRef: ChangeDetectorRef,
    private patientSessionService: PatientSessionService,
    private alertService: AlertService
  ) {
    super(cdRef);
    effect(() => {
      this.patientSessionService.dataChanged();
      const targetData = this.patientSessionService.getTargetData(this.targetID);
      if (targetData) {
        this.targetData = targetData.filter(t => !t.DeletedDate);
        this.remainingTrials = (this.target?.DesiredTrials ?? 0) - this.targetData.length;
        if (this.remainingTrials < 0) {
          this.remainingTrials = 0;
        }

        if (this.target?.TargetStatus === PatientTargetStatus.Mastered || this.target?.TargetStatus === PatientTargetStatus.Closed || this.target?.TargetStatus === PatientTargetStatus.Fluent) {
          this.remainingTrials = 0;
        }

        this.rates = this.targetData.map(t => {
          return {
            totalCount: t.IntValue ?? 0,
            Duration: t.Duration ?? 0,
            RecordedDate: t.RecordedDate ?? '',
            FormattedDate: this.formatDate(new Date(t.RecordedDate ?? '')),
          };
        });
      }
      else {
        this.targetData = [];
        this.rates = [];
      }
    });

    effect(() => {
      const targetsRunningData = this.patientSessionService.getTargetsRunningData(this.patientSessionService.getPatientSession()?.ID ?? -1,this.targetID);
      if (targetsRunningData) {
        this.isStarted = targetsRunningData.Data.isStarted;
        this.startTime = targetsRunningData.Data.startTime;
        this.timerValue = this.isStarted
          ? this.getCurrentTimerValue()
          : targetsRunningData.Data.timerValue;
        this.totalCount = targetsRunningData.Data.totalCount ?? 0;
        if (this.isStarted) {
          if (this.timeOut) {
            clearTimeout(this.timeOut);
          }
          this.timeOut = setTimeout(() => {
            this.updateTimer();
          }, 10);
        }
      } else {
        this.isStarted = false;
        this.startTime = 0;
        this.timerValue = this.getInitialTimerValue();
        this.totalCount = 0;
      }
    });
  }

  ngOnInit() {
    this.text = this.target?.TargetName ?? '-';
    this.targetID = this.target?.ID ?? 0;
    this.expectedAmount = this.target?.DesiredTrials ?? 1;
    this.timerInitialValue = this.isOpenEndedTimer()
      ? 0
      : this.target?.Duration ?? 60;
    this.timerValue = this.getInitialTimerValue();
  }

  override doLongPress(): void {
    this.showExtra2();
  }

  startTimer() {
    if (this.isStarted) {
      this.showExtra();
    } else {
      this.isStarted = true;
      this.startTime = Date.now();
      this.totalCount = 0;
      this.updateTimer();
      this.saveTargetRunningData();
      this.showExtra();
    }
  }

  openRate() {
    if (this.isStarted) {
      this.showExtra();
    }
  }

  saveTargetRunningData() {
    this.patientSessionService.setTargetsRunningData(this.patientSessionService.getPatientSession()?.ID ?? -1, this.targetID, { isRateTarget:true, isOpenEndedFluencyRate: this.isOpenEndedTimer(), isStarted: this.isStarted, startTime: this.startTime, timerValue: this.timerValue, totalCount: this.totalCount });
  }

  private updateTimer(): void {
    if (this.isStarted) {
      this.timerValue = this.getCurrentTimerValue();

      if (this.isOpenEndedTimer()) {
        this.scheduleNextTimerUpdate();
        return;
      }

      const shouldRingAlarm = this.timerValue === 0 &&
        (this.target?.AlarmOption === AlarmOption.AlarmAtEnd);

      this.ringAlarmIfNeeded(
        this.target?.AlarmOption ?? AlarmOption.NoAlarm,
        this.timerValue,
        this.target?.Duration ?? 0,
        this.target?.AlarmSoundOption ?? 1,
        false
      );

      if (this.timerValue <= 0) {
        if (shouldRingAlarm) {
          setTimeout(() => {
            this.stopTimer();
          }, 100);
        } else {
          this.stopTimer();
        }
      } else {
        this.scheduleNextTimerUpdate();
      }
    }else {
      if (this.timeOut) {
        clearTimeout(this.timeOut);
      }
    }
  }

  private getCurrentTimerValue(): number {
    const elapsedSeconds = this.getElapsedSeconds();
    if (this.isOpenEndedTimer()) {
      return elapsedSeconds;
    }

    return Math.max(
      0,
      this.timerInitialValue - elapsedSeconds
    );
  }

  private getElapsedSeconds(): number {
    if (!this.startTime) {
      return 0;
    }

    return Math.max(0, Math.floor((Date.now() - this.startTime) / 1000));
  }

  private getInitialTimerValue(): number {
    return this.isOpenEndedTimer() ? 0 : this.timerInitialValue;
  }

  private isOpenEndedTimer(): boolean {
    return targetIsOpenEndedTimer(this.target);
  }

  private scheduleNextTimerUpdate(): void {
    if (this.timeOut) {
      clearTimeout(this.timeOut);
    }
    this.timeOut = setTimeout(() => {
      this.updateTimer();
      this.saveTargetRunningData();
    }, 1000);
  }

  override ngOnDestroy() {
    if (this.timeOut) {
      clearTimeout(this.timeOut);
    }
    super.ngOnDestroy();
  }

  stopTimer() {
    if (this.timeOut) {
      clearTimeout(this.timeOut);
    }
    const elapsedSeconds = this.getElapsedSeconds();
    if (elapsedSeconds > 0 || this.totalCount > 0) {
      const currentDate = new Date();
      this.patientSessionService.addTargetData({
        IntValue: this.totalCount,
        TextValue: `${this.totalCount}`,
        Duration: Math.max(elapsedSeconds, 1),
        PatientTargetID: this.targetID,
        RecordedDate: currentDate.toServerString(),
      });
    }
    this.timerValue = this.getInitialTimerValue();
    this.isStarted = false;
    this.totalCount = 0;
    this.startTime = 0;

    this.saveTargetRunningData();
    this.closeExtra();
  }


  timerString() {
    const minutes = Math.floor(this.timerValue / 60);
    const seconds = this.timerValue % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  lastRate() {
    if (this.rates.length > 0) {
      const lastRate = this.rates[this.rates.length - 1];
      return `${lastRate.totalCount}`;
    } else {
      return '';
    }
  }

  incrementCount(event?: Event) {
    event?.stopPropagation();
    this.totalCount++;
    this.saveTargetRunningData();
  }

  formattedDuration(durationSeconds: number) {
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = durationSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  deleteData(data?: any) {
    if (data) {
      const confirmed = this.alertService.confirm('Are you sure you want to delete this data?', 'Delete Data');
      confirmed.subscribe(result => {
        if (result) {
          let targetDataToSave: PatientTargetDataModel | undefined = this.targetData.find(t =>
            t.RecordedDate === data.RecordedDate
          );

          if (targetDataToSave) {
            targetDataToSave.DeletedDate = new Date().toServerString();
            this.patientSessionService.saveTargetData(targetDataToSave);
          }
        }
      });
    }
  }
}
